import type { Kennzahl, GetValuesFn } from './types'

/**
 * Aggregiert die beiden Sub-Kennzahlen `{prefix}1.` und `{prefix}2.` pro Semester
 * zu einem einzigen Dataset (Summe je Semester).
 *
 * Verwendet `kennzahl.sourceKennzahlId ?? kennzahl.kennzahlId` als Präfix,
 * damit eine Kennzahl (z.B. '3.1.') Daten aus einer anderen Quelle (z.B. '3.2.')
 * aggregiert anzeigen kann.
 *
 * @param kennzahl     - Kennzahl-Konfiguration
 * @param getValues    - reaktive Datenfunktion (nach Semester gefiltert & sortiert)
 * @param baseYears    - Semester-Achsenbeschriftungen
 * @param selectedType - aktuell ausgewählter Diagrammtyp (bestimmt die Farbe)
 */
export function buildSummedSubsChartData(
    kennzahl: Kennzahl,
    getValues: GetValuesFn,
    baseYears: string[]
): any {
    const prefix = kennzahl.sourceKennzahlId ?? kennzahl.kennzahlId
    const valuesOne = getValues(`${prefix}1.`)
    const valuesTwo = getValues(`${prefix}2.`)

    if (!valuesOne.length && !valuesTwo.length) return false

    // Semesterwerte beider Sub-Kennzahlen summieren
    const sumMap = new Map<string, number>()
    ;[...valuesOne, ...valuesTwo].forEach((v) => {
        sumMap.set(v.semester, (sumMap.get(v.semester) ?? 0) + Number(v.wert))
    })

    // Sortierung nach year-Code über valuesOne/valuesTwo rekonstruieren
    const allValues = [...valuesOne, ...valuesTwo]
    const semesterOrder = [...new Map(allValues.map((v) => [v.semester, v.year])).entries()]
        .sort((a, b) => a[1] - b[1])
        .map(([semester]) => semester)

    const data: { x: string; y: number }[] = semesterOrder.map((semester) => ({
        x: semester,
        y: sumMap.get(semester)!
    }))

    const maxRaw = Math.max(...data.map((d) => d.y))
    const maxValue = maxRaw ? maxRaw * 1.15 : 10

    return {
        labels: baseYears,
        datasets: [
            {
                label: 'IST-Wert',
                data,
                backgroundColor: '#009EE3',
                borderColor: '#009EE3',
                fill: false,
                maxBarThickness: 41,
                minBarLength: 7,
                isPercentValue: false,
                maxValue
            }
        ]
    }
}
